import { useCallback, useRef } from 'react';

import { useApi } from '@backstage/core-plugin-api';

import { Action, Interaction, trackingApiRef } from '../../apis';
import { readSourceAnchor } from '../../inference/sourceAnchor';
import { ElementId } from '../../types';
import {
  collectDomScopes,
  observeImpressions,
  parseElementId,
  parseTrackData,
} from '../../utils';

export function useAutoTracking(): React.RefCallback<HTMLElement> {
  const tracking = useApi(trackingApiRef);

  const handleClick = useCallback(
    (event: Event) => {
      const container = event.currentTarget as HTMLElement;
      let target = event.target as HTMLElement | null;

      // Walk up from target to find data-track-id
      let trackedElement: HTMLElement | null = null;
      while (target && target !== container) {
        if (target.dataset.trackId) {
          trackedElement = target;
          break;
        }
        target = target.parentElement;
      }
      // Also check the container itself
      if (!trackedElement && container.dataset.trackId) {
        trackedElement = container;
      }
      if (!trackedElement) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.debug(
            '[AutoTracking] Click on element without data-track-id. Add data-track-id and data-track-action to enable tracking.',
            event.target,
          );
        }
        return;
      }

      // Collect data from the tracked element itself
      const elementData = parseTrackData(trackedElement);
      // Walk from tracked element up to container, collecting DOM scopes and data
      const domResult = collectDomScopes(trackedElement, container);
      // Also collect scopes and data above the container
      const outerResult = collectDomScopes(container);
      // Also collect data from the container element itself
      const containerData = parseTrackData(container);

      const trackId = trackedElement.dataset.trackId;
      if (!trackId) return;

      const elementId = parseElementId(trackId as ElementId);
      const elementPath = [
        ...outerResult.scopes,
        ...domResult.scopes,
        elementId,
      ];

      const action = trackedElement.dataset.trackAction as Action | undefined;
      const type = (trackedElement.dataset.trackType ??
        'click') as Interaction['type'];

      if (!action) return;

      const data = Object.assign(
        {},
        outerResult.data,
        containerData,
        domResult.data,
        elementData,
      );

      const sourceAnchor = readSourceAnchor(trackedElement)?.anchor ?? null;

      tracking.trackInteraction({
        quality: 'declared',
        sourceAnchor,
        elementPath,
        type,
        action,
        data,
      });
    },
    [tracking],
  );

  const prevRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback(
    (node: HTMLElement | null) => {
      if (prevRef.current) {
        prevRef.current.removeEventListener('click', handleClick);
      }
      cleanupRef.current?.();
      cleanupRef.current = null;

      if (node) {
        node.addEventListener('click', handleClick);
        cleanupRef.current = observeImpressions(node, tracking);
      }
      prevRef.current = node;
    },
    [handleClick, tracking],
  );
}
